import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { BUTTON_LAYOUT, BUTTON_SIZES, BUTTON_SURFACES } from './buttonSurface';
import type { ButtonSize, ButtonVariant } from './buttonSurface';
import { useInModalFooter } from './modalFooterSurface';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** `md` is the standard button. `sm` (32px) is for a table action gutter and nowhere else. */
  size?: ButtonSize;
  /** Swaps the label for a spinner and disables the control. */
  loading?: boolean;
  /** Shown in place of the label while `loading` — present-progressive, e.g. "Signing in". */
  loadingLabel?: string;
  leading?: ReactNode;
  fullWidth?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  loadingLabel,
  leading,
  fullWidth = false,
  children,
  className = '',
  disabled,
  type = 'button',
  tabIndex,
  ...rest
}: ButtonProps) {
  /*
    The tab run through a modal is the fields, the confirm tick, then the primary action. A
    footer's other buttons are out of it: Cancel repeats what Esc already does, and
    Autogenerate is a shortcut for filling fields the reader can still fill by hand — neither
    loses a function, and both cost a stop before the button the run exists to reach.

    Read from context rather than asked of the caller, because a rule that has to be
    remembered on every new modal footer is one that gets missed. An explicit `tabIndex`
    still wins, for the footer that has a reason.
  */
  const inModalFooter = useInModalFooter();
  const isAction = variant === 'primary' || variant === 'danger';
  const resolvedTabIndex = tabIndex ?? (inModalFooter && !isAction ? -1 : undefined);

  return (
    <button
      {...rest}
      tabIndex={resolvedTabIndex}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        BUTTON_LAYOUT,
        BUTTON_SIZES[size],
        'rounded-lg focus-visible:ring-2 focus-visible:ring-brand-300',
        fullWidth ? 'w-full' : '',
        BUTTON_SURFACES[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : leading}
      <span>{loading && loadingLabel ? loadingLabel : children}</span>
    </button>
  );
}
